'use client';

import type { FC } from 'react';
import { m, useReducedMotion } from 'motion/react';
import { usePathname } from 'next/navigation';

import { BlogGallery } from '@/components/blog-gallery/blog-gallery';
import { Heading } from '@/components/ui/heading/heading';
import { SectionLink } from '@/components/ui/section-link/section-link';
import type { TApiHeading, TApiLink } from '@/shared/apiComponentTypes';
import { TBlogPost } from '@/app/blog/[slug]/page';
import { fadeSlideUp, staggerContainer } from '@/utils/animation/variants';

export type TBlogSection = [TApiHeading, TApiLink];

export const BlogSection: FC<{ content: TBlogSection; data: TBlogPost[] }> = props => {
  const prefersReducedMotion = useReducedMotion();
  const pathname = usePathname();

  return (
    <m.div
      key={pathname}
      initial={prefersReducedMotion ? false : 'hidden'}
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      variants={staggerContainer}
    >
      <m.div variants={fadeSlideUp}>
        <div className="inline-block mb-2 text-xs font-oswald uppercase tracking-[0.35em] text-neutral-400">
          <span className="font-oswald">{props.content[0].caption}</span>
          <div className="flex items-center gap-2 mt-2 w-3/4">
            <div className="h-1 basis-1/3 rounded-full bg-action" />
            <div className="h-[2px] basis-2/3 rounded-full bg-neutral-700/70" />
          </div>
        </div>

        <Heading variant="h2" className="mb-10" highlight={props.content[0].highlight}>
          {props.content[0].title}
        </Heading>
      </m.div>

      <m.div variants={fadeSlideUp}>
        <BlogGallery items={props.data} />
      </m.div>

      <m.div variants={fadeSlideUp} className="flex justify-end mt-10">
        <SectionLink href={props.content[1].href} label={props.content[1].label} />
      </m.div>
    </m.div>
  );
};
